/**
 * SEO utility — updates document title, meta description, canonical, OG/Twitter
 * tags, hreflang alternates and JSON-LD per page and per language.
 * No external dependencies. Bots receive the result through the prerender step
 * (prerender.js) so nothing here needs JavaScript on their side.
 *
 * Copy rules (see the Google "title link", "site name" and "sitelinks" docs):
 * - Home title is brand + a slogan written for each locale ("Nexuni | AI Robots
 *   to Scale Your Workforce" / "Nexuni | AI 機器人自動化專家" / "Nexuni | 現場で働く
 *   AI ロボット"); the domain root serves the zh one. It is
 *   deliberately NOT the hero H1 ("AI Robots, Ready to Work."): when the title
 *   minus the brand equalled the H1, Google showed the H1 alone and dropped
 *   "Nexuni |" from the title link. The tagline opens the EN description so
 *   the phrase exists on the page. Do not change the H1 to match the title.
 * - Every other title is "Page | Nexuni". Google turns these into sitelink
 *   labels and strips the "| Nexuni" boilerplate, so keep the page part short
 *   and identical to the nav label and page H1 (Products, About Us, Careers,
 *   產品, 關於我們, 製品 ...). No two pages may share a title: job pages pass
 *   their own title through the updateSEO overrides argument.
 * - Descriptions stay under ~150 Latin or ~70 CJK characters so the snippet is
 *   not truncated; product keywords (odo series, autonomous inspection
 *   quadruped) live in descriptions and on product pages, not in the home title.
 * - The site name shown above the title link is per domain and comes from the
 *   WebSite JSON-LD in public/index.html ("Nexuni"). Google also reads the
 *   brand before the delimiter in the home <title>, so the brand part of every
 *   home title is exactly "Nexuni" and "Nexuni 耐思尼" is never an
 *   alternateName (耐思尼 lives in descriptions only). Do not put "耐思尼" in
 *   sub-page titles, it would survive the boilerplate strip and clutter sitelinks.
 * - No unverifiable claims (labour-saving percentages etc.).
 * - hreflang: zh-TW, zh (alias of zh-TW), en, ja, and x-default = en. Keep
 *   updateHreflang, public/index.html and scripts/gen-sitemap.js in sync.
 */

const BASE_URL = 'https://www.nexuni.com';
const DEFAULT_IMAGE = BASE_URL + '/og-image.jpg';

const PAGE_SEO = {
  home: {
    title: {
      zh: 'Nexuni | AI 機器人自動化專家',
      en: 'Nexuni | AI Robots to Scale Your Workforce',
      ja: 'Nexuni | 現場で働く AI ロボット'
    },
    description: {
      zh: 'Nexuni 耐思尼是台灣的 AI 機器人公司。NIRO 大腦驅動 odo 系列自主巡檢機器狗、智慧焊接機器人與 AI 自動化系統，已部署超過 100 套。',
      en: 'AI Robots to Scale Your Workforce. Nexuni, a Taiwan AI robotics company. NIRO drives the odo series inspection quadruped, welding robots and AI automation.',
      ja: 'Nexuni は台湾の AI ロボティクス企業。NIRO の頭脳が odo シリーズ自律巡回四足ロボット、スマート溶接ロボット、AI 自動化を駆動。100 以上のシステムを導入。'
    }
  },
  solutions: {
    title: {
      zh: '產品 | Nexuni',
      en: 'Products | Nexuni',
      ja: '製品 | Nexuni'
    },
    description: {
      zh: 'Nexuni 全系列產品：odo 系列自主巡檢機器狗、智慧焊接機器人、AI 智慧停車管理、車牌辨識主機、影像車位導引與 AI 影像保全。',
      en: 'Nexuni products: the odo series autonomous inspection quadruped, intelligent welding robot, AI parking management, LPR engine, parking guidance and AI video security.',
      ja: 'Nexuni の製品：odo シリーズ自律巡回四足ロボット、スマート溶接ロボット、AI 駐車管理、車番認識エンジン、駐車誘導、AI 映像セキュリティ。'
    }
  },
  about_us: {
    title: {
      zh: '關於我們 | Nexuni',
      en: 'About Us | Nexuni',
      ja: '会社について | Nexuni'
    },
    description: {
      zh: 'Nexuni 耐思尼成立於新竹科學園區，團隊來自 MIT、CMU、台大、清大、陽明交大，掌握 AI 機器人完整核心技術；NVIDIA Inception 成員與 Universal Robots 認證夥伴，據點台灣與新加坡。',
      en: 'Nexuni was founded in Hsinchu Science Park by a team from MIT, CMU, NTU, NTHU and NYCU that owns the full AI robotics stack. An NVIDIA Inception member and Universal Robots certified partner with offices in Taiwan and Singapore.',
      ja: 'Nexuni は新竹サイエンスパークで設立。MIT・CMU・台湾大学・清華大学・陽明交通大学出身のチームが AI ロボットのコア技術を保有。NVIDIA Inception メンバー、Universal Robots 認定パートナー。台湾とシンガポールに拠点。'
    }
  },
  core_technology: {
    title: {
      zh: '核心技術 | Nexuni',
      en: 'Core Technology | Nexuni',
      ja: 'コア技術 | Nexuni'
    },
    description: {
      zh: 'NIRO 是 Nexuni 機器人的 AI 大腦：整合多模態感知、SLAM 定位、規劃與控制，以模擬環境中的強化學習訓練四足機器人，在機器人上的 NVIDIA Jetson 本地推論，讓機器人與自動化系統進場就能上工。',
      en: 'NIRO is the AI brain in every Nexuni robot: multimodal perception, SLAM, planning and control, quadruped locomotion trained with reinforcement learning in simulation, and local inference on NVIDIA Jetson on the robot.',
      ja: 'NIRO は Nexuni のロボットの AI 頭脳。マルチモーダル感知、SLAM、計画と制御を統合し、シミュレーション上の強化学習で四足ロボットを訓練。ロボット上の NVIDIA Jetson でローカル推論。'
    }
  },
  colab_flow: {
    title: {
      zh: '合作流程 | Nexuni',
      en: 'How to Collaborate | Nexuni',
      ja: '協業プロセス | Nexuni'
    },
    description: {
      zh: '兩種合作模式：標準合作四步驟（選擇產品、諮詢、報價、部署）快速導入 AI 機器人；首發客戶模式從 MOU、前線部署、提案到設計開發，打造專屬的機器人自動化方案。',
      en: 'Two ways to work with Nexuni: the standard four-step path (select, consult, quote, deploy) or the Launch Customer programme, from MOU and forward deployment to custom design and development.',
      ja: 'Nexuni との 2 つの協業モデル：製品選定・相談・見積・導入の 4 ステップの標準導入と、MOU 締結・現場派遣・提案・設計開発で専用ソリューションを構築するローンチカスタマー。'
    }
  },
  career: {
    title: {
      zh: '人才招募 | Nexuni',
      en: 'Careers | Nexuni',
      ja: '採用情報 | Nexuni'
    },
    description: {
      zh: 'Nexuni 徵求機器人軟體工程師、AI 軟體工程師、硬體設計工程師與實習生，一起打造進場就能上工的 AI 機器人。',
      en: 'Nexuni is hiring robot software engineers (ROS 2, SLAM), AI engineers, hardware engineers and interns to build AI robots that go to work on real sites.',
      ja: 'Nexuni ではロボットソフトウェアエンジニア（ROS 2、SLAM）、AI エンジニア、ハードウェアエンジニア、インターンを募集中。現場で働く AI ロボットを一緒に開発しましょう。'
    }
  },
  contact: {
    title: {
      zh: '聯絡我們 | Nexuni',
      en: 'Contact Us | Nexuni',
      ja: 'お問い合わせ | Nexuni'
    },
    description: {
      zh: '預約現場評估或洽詢 odo 系列自主巡檢機器狗、智慧焊接與 AI 自動化方案。Nexuni 耐思尼位於新竹科學園區，服務台灣、新加坡與全球客戶。',
      en: 'Book a site assessment or ask about the odo series inspection quadruped, intelligent welding and AI automation. Nexuni is based in Hsinchu Science Park and serves Taiwan, Singapore and worldwide.',
      ja: '現場アセスメントの予約、odo シリーズ自律巡回四足ロボット、スマート溶接、AI 自動化に関するお問い合わせ。Nexuni は新竹サイエンスパークを拠点に台湾・シンガポール・世界各地へサービスを提供。'
    }
  },
  stories: {
    title: {
      zh: '客戶實績 | Nexuni',
      en: 'Case Studies | Nexuni',
      ja: '導入事例 | Nexuni'
    },
    description: {
      zh: 'Nexuni AI 機器人導入實績：智慧焊接產線、odo 系列機器狗的設施巡邏、AI 停車場管理等真實案例，超過 100 套系統在工廠、社區與停車場穩定運行。',
      en: 'Nexuni deployments: intelligent welding lines, facility patrol with the odo series quadruped, and AI parking management. Over 100 systems running in factories, communities and car parks.',
      ja: 'Nexuni の導入事例：スマート溶接ライン、odo シリーズ四足ロボットによる施設巡回、AI 駐車場管理。100 以上のシステムが工場・集合住宅・駐車場で稼働中。'
    }
  },
  blog: {
    title: {
      zh: '部落格 | Nexuni',
      en: 'Blog | Nexuni',
      ja: 'ブログ | Nexuni'
    },
    description: {
      zh: 'Nexuni 部落格：odo 系列機器狗的部署紀錄、展覽活動、AI 機器人技術與合作夥伴動態。',
      en: 'The Nexuni blog: odo series deployments, exhibitions, AI robotics engineering notes and partner news.',
      ja: 'Nexuni ブログ：odo シリーズの導入記録、展示会、AI ロボティクスの技術ノート、パートナー情報。'
    }
  },
  news: {
    title: {
      zh: '最新消息 | Nexuni',
      en: 'News | Nexuni',
      ja: 'ニュース | Nexuni'
    },
    description: {
      zh: 'Nexuni 最新消息：產品發表、展覽活動、媒體報導與合作夥伴動態。',
      en: 'Latest from Nexuni: product launches, exhibitions, press coverage and partnership updates.',
      ja: 'Nexuni の最新ニュース：製品発表、展示会、メディア掲載、パートナーシップ情報。'
    }
  },
  security_policy: {
    title: {
      zh: '資訊安全政策 | Nexuni',
      en: 'Information Security Policy | Nexuni',
      ja: '情報セキュリティポリシー | Nexuni'
    },
    description: {
      zh: 'Nexuni 耐思尼的資訊安全政策：資料保護、存取控制與機器人系統的安全承諾。',
      en: "Nexuni's information security policy: data protection, access control and security commitments for our robot systems.",
      ja: 'Nexuni の情報セキュリティポリシー：データ保護、アクセス制御、ロボットシステムのセキュリティに関する方針。'
    }
  },
  internSE2022Spring: {
    title: { zh: '軟體實習心得 | Nexuni', en: 'Software Intern Story | Nexuni', ja: 'ソフトウェアインターン体験談 | Nexuni' },
    description: {
      zh: 'Nexuni 軟體實習生的親身分享：在 AI 機器人公司實習的日常、專案與收穫。',
      en: 'A Nexuni software intern shares the day-to-day, the projects and the lessons of interning at an AI robotics company.',
      ja: 'Nexuni のソフトウェアインターンが語る、AI ロボティクス企業でのインターンの日常、プロジェクト、学び。'
    }
  },
  internSE2022Spring_wu: {
    title: { zh: '軟體實習心得 | Nexuni', en: 'Software Intern Story | Nexuni', ja: 'ソフトウェアインターン体験談 | Nexuni' },
    description: {
      zh: 'Nexuni 軟體實習生的親身分享：在 AI 機器人公司實習的日常、專案與收穫。',
      en: 'A Nexuni software intern shares the day-to-day, the projects and the lessons of interning at an AI robotics company.',
      ja: 'Nexuni のソフトウェアインターンが語る、AI ロボティクス企業でのインターンの日常、プロジェクト、学び。'
    }
  },
  internME2022Spring: {
    title: { zh: '硬體實習心得 | Nexuni', en: 'Hardware Intern Story | Nexuni', ja: 'ハードウェアインターン体験談 | Nexuni' },
    description: {
      zh: 'Nexuni 硬體實習生的親身分享：機器人硬體設計實習的日常與收穫。',
      en: 'A Nexuni hardware intern shares what it is like to design robot hardware as an intern.',
      ja: 'Nexuni のハードウェアインターンが語る、ロボットハードウェア設計インターンの日常と学び。'
    }
  }
};

/**
 * Product pages. `name` is the plain product name used in JSON-LD and
 * breadcrumbs; `image` is the social/preview image (absolute path under
 * public/); `models` lists sellable variants for the Product schema.
 */
const PRODUCT_SEO = {
  odo: {
    name: { zh: '自主巡檢機器狗 odo 系列', en: 'odo Series Autonomous Inspection Quadruped', ja: '自律巡回四足ロボット odo シリーズ' },
    title: {
      zh: 'odo 系列自主巡檢機器狗 | Nexuni',
      en: 'odo Series Autonomous Inspection Quadruped | Nexuni',
      ja: 'odo シリーズ 自律巡回四足ロボット | Nexuni'
    },
    description: {
      zh: 'odo 系列自主巡檢機器狗：可 24/7 自主巡邏與巡檢的四足機器人，讀取儀表、熱像與氣體，跨樓層走樓梯、斜坡與電梯，每個檢查點留下影像證據。odo 1 入門款、odo 3 進階款、odo 9 旗艦款（Nexuni 全自製、IP67）。',
      en: 'The odo series autonomous patrol and inspection quadruped: 24/7 rounds that read gauges, thermal images and gas, take stairs, slopes and elevators, and leave photo evidence at every checkpoint. odo 1 entry, odo 3 advanced, odo 9 flagship (fully Nexuni-made, IP67).',
      ja: 'odo シリーズ自律巡回四足ロボット：24 時間 365 日の自律巡回・点検で計器・熱画像・ガスを読み取り、階段・斜面・エレベーターで複数階を巡回。各チェックポイントに写真の証拠を残します。odo 1 エントリー、odo 3 アドバンスト、odo 9 フラッグシップ（Nexuni 完全自社製、IP67）。'
    },
    image: '/assets/odo/hero/odo9_substation.jpg',
    images: ['/assets/odo/hero/odo9_substation.jpg', '/assets/odo/odo9.png'],
    category: 'Autonomous inspection robot (quadruped)',
    models: [
      { name: 'nexuni odo 1', mpn: 'NXBOT-ODO1' },
      { name: 'nexuni odo 3', mpn: 'NXBOT-ODO3' },
      { name: 'nexuni odo 9', mpn: 'NXBOT-ODO9' }
    ],
    keywords: ['autonomous inspection quadruped', 'autonomous patrol robot', 'quadruped inspection robot', 'robot dog inspection', 'odo series', 'nexuni odo', '自主巡檢機器狗', '巡檢機器狗', '四足機器人', '自律巡回四足ロボット']
  },
  beaver: {
    name: { zh: '智慧焊接機器人', en: 'Intelligent Welding Robot', ja: 'スマート溶接ロボット' },
    title: {
      zh: '智慧焊接機器人 | Nexuni',
      en: 'Intelligent Welding Robot | Nexuni',
      ja: 'スマート溶接ロボット | Nexuni'
    },
    description: {
      zh: 'Nexuni 智慧焊接機器人結合雷射掃描與 Universal Robots 協作手臂，逐件掃描、自動修正工件誤差，不需治具也不需專業焊工守在教導器前。適用金屬加工與製造業。',
      en: 'The Nexuni intelligent welding robot pairs laser scanning with Universal Robots collaborative arms: it scans each piece, corrects workpiece deviations automatically, and needs no jigs or skilled welder at the pendant.',
      ja: 'Nexuni スマート溶接ロボットはレーザースキャンと Universal Robots 協働アームを組み合わせ、一品ごとにスキャンしてワーク誤差を自動補正。治具も、ティーチペンダントに付く熟練溶接工も不要。金属加工・製造業向け。'
    },
    category: 'Welding robot'
  },
  pms100: {
    name: { zh: 'AI 智慧停車管理系統', en: 'AI Parking Management System', ja: 'AI 駐車管理システム' },
    title: {
      zh: 'AI 智慧停車管理系統 | Nexuni',
      en: 'AI Parking Management System | Nexuni',
      ja: 'AI 駐車管理システム | Nexuni'
    },
    description: {
      zh: 'Nexuni AI 智慧停車管理系統：車牌辨識、繳費與導引一體化，AI 邊緣運算，支援現金、信用卡與 QR Code 支付，適用各類停車場的無人化管理。',
      en: 'Nexuni AI parking management: license plate recognition, payment and guidance in one system with AI edge computing. Cash, card and QR code payment for unattended car parks of any size.',
      ja: 'Nexuni AI 駐車管理システム。車番認識・決済・誘導を一体化し、AI エッジ推論で稼働。現金・クレジットカード・QR コード決済に対応、あらゆる駐車場の無人化管理に。'
    }
  },
  pms100le: {
    name: { zh: '車牌辨識主機', en: 'LPR Engine', ja: '車番認識エンジン' },
    title: {
      zh: '車牌辨識主機 | Nexuni',
      en: 'LPR Engine | Nexuni',
      ja: '車番認識エンジン | Nexuni'
    },
    description: {
      zh: 'Nexuni 車牌辨識主機，體積小於手機，低功耗 AI 邊緣運算，開放式 API 架構，輕鬆串接各類停車管理系統。',
      en: 'The Nexuni LPR engine: smaller than a phone, low-power AI edge computing and an open API for integration with any parking management system.',
      ja: 'Nexuni 車番認識エンジン。スマホより小型、低消費電力の AI エッジ推論、オープン API で既存の駐車管理システムに柔軟に接続。'
    }
  },
  vpgs100: {
    name: { zh: '智慧影像車位導引系統', en: 'Vision-Based Parking Guidance System', ja: '映像式駐車誘導システム' },
    title: {
      zh: '智慧影像車位導引系統 | Nexuni',
      en: 'Vision-Based Parking Guidance System | Nexuni',
      ja: '映像式駐車誘導システム | Nexuni'
    },
    description: {
      zh: 'Nexuni 智慧影像車位導引系統，AI 即時偵測空位並引導車輛，提升停車場營運效率與使用者體驗。',
      en: 'The Nexuni vision-based parking guidance system detects free spaces in real time with AI and guides drivers to them, improving car park operations and user experience.',
      ja: 'Nexuni 映像式駐車誘導システム。AI がリアルタイムで空き状況を検知して車両を誘導し、駐車場の運営効率とユーザー体験を向上。'
    }
  },
  vs100: {
    name: { zh: 'AI 智慧影像保全', en: 'AI Video Security', ja: 'AI 映像セキュリティ' },
    title: {
      zh: 'AI 智慧影像保全系統 | Nexuni',
      en: 'AI Video Security System | Nexuni',
      ja: 'AI 映像セキュリティシステム | Nexuni'
    },
    description: {
      zh: 'Nexuni AI 智慧影像保全：同時監看每一支攝影機，入侵與越線即時告警並附影像證據，大幅降低人力監看需求，可與 odo 系列機器狗聯動前往現場確認。',
      en: 'Nexuni AI video security watches every camera at once, flags intrusions and line-crossings instantly with video evidence, cuts manual monitoring, and can dispatch an odo series quadruped to check the scene.',
      ja: 'Nexuni AI 映像セキュリティ。全カメラを同時に監視し、侵入・ライン越えを映像の証拠つきで即時通知。人的監視を大幅に削減し、odo シリーズ四足ロボットを現場確認に派遣することも可能。'
    }
  }
};

/**
 * Convert internal lang code to URL slug.
 * 'zh' → 'zh-tw', others pass through.
 */
function langToSlug(lang) {
  if (lang === 'zh' || lang === 'zh-tw') return 'zh-tw';
  return lang;
}

function normLang(lang) {
  return (lang === 'zh-tw' || lang === 'zh') ? 'zh' : lang;
}

function pick(obj, l) {
  return (obj && (obj[l] || obj.en || obj.zh)) || '';
}

/** Shared head updates for every page: lang, title, description, canonical, social tags, hreflang. */
function applyHead(seo, lang, routePath, image) {
  const l = normLang(lang);
  const slug = langToSlug(lang);

  // HTML lang attribute (critical for Googlebot language detection)
  document.documentElement.setAttribute('lang', l === 'zh' ? 'zh-TW' : l);

  const title = pick(seo.title, l);
  const description = pick(seo.description, l);
  document.title = title;
  setMeta('description', description);

  // Canonical — always uses the correct slug in the URL
  const canonical = BASE_URL + '/' + slug + routePath;
  setLink('canonical', canonical);

  // Open Graph
  setMeta('og:title', title, 'property');
  setMeta('og:description', description, 'property');
  setMeta('og:url', canonical, 'property');
  setMeta('og:image', image || DEFAULT_IMAGE, 'property');
  setMeta('og:locale', l === 'zh' ? 'zh_TW' : (l === 'ja' ? 'ja_JP' : 'en_US'), 'property');

  // Twitter
  setMeta('twitter:title', title);
  setMeta('twitter:description', description);
  setMeta('twitter:image', image || DEFAULT_IMAGE);

  updateHreflang(routePath);
  return { l, slug, canonical, title, description };
}

/**
 * Update page SEO: title, description, canonical, OG tags and hreflang.
 * @param {string} pageKey - Key in PAGE_SEO (e.g. 'home', 'solutions')
 * @param {string} lang - Internal lang code ('zh', 'en', 'ja') or slug ('zh-tw')
 * @param {string} routePath - Route path WITHOUT lang prefix (e.g. '/', '/solutions', '/about/about_us')
 * @param {object} [overrides] - Optional { title, description } tri-language objects that replace the PAGE_SEO ones (used by job pages).
 */
function updateSEO(pageKey, lang, routePath, overrides) {
  const base = PAGE_SEO[pageKey] || PAGE_SEO['home'];
  const seo = overrides ? Object.assign({}, base, overrides) : base;
  applyHead(seo, lang, routePath, null);
  setJsonLd('seo-product', null);
}

/**
 * Update SEO for product/tech pages: head tags plus Product JSON-LD.
 * @param {string} productId - Product key (e.g. 'odo', 'beaver')
 * @param {string} lang - Internal lang code ('zh', 'en', 'ja') or slug ('zh-tw')
 * @param {string} routePath - Route path WITHOUT lang prefix (e.g. '/tech/odo')
 */
function updateProductSEO(productId, lang, routePath) {
  const seo = PRODUCT_SEO[productId];
  if (!seo) {
    updateSEO('solutions', lang, routePath);
    return;
  }
  const image = seo.image ? BASE_URL + seo.image : null;
  const head = applyHead(seo, lang, routePath, image);
  const name = pick(seo.name, head.l) || head.title.split('|')[0].trim();

  const product = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: name,
    description: head.description,
    url: head.canonical,
    image: (seo.images || (seo.image ? [seo.image] : [])).map(function (p) { return BASE_URL + p; }),
    brand: { '@type': 'Brand', name: productId === 'odo' ? 'nexuni odo' : 'Nexuni' },
    manufacturer: { '@type': 'Organization', name: 'Nexuni', url: BASE_URL },
    category: seo.category || 'AI robot and automation system'
  };
  if (product.image.length === 0) delete product.image;
  if (seo.models) {
    product.model = seo.models.map(function (m) {
      return { '@type': 'ProductModel', name: m.name, mpn: m.mpn };
    });
  }
  if (seo.keywords) product.keywords = seo.keywords.join(', ');
  setJsonLd('seo-product', product);
  // BreadcrumbList JSON-LD is rendered by the Breadcrumb component on every page.
}

// --- Helpers ---

/** Upserts (or removes, when data is null) an inline JSON-LD script identified by id. */
function setJsonLd(id, data) {
  var el = document.getElementById(id);
  if (!data) {
    if (el) el.parentNode.removeChild(el);
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function setMeta(name, content, attr) {
  attr = attr || 'name';
  var el = document.querySelector('meta[' + attr + '="' + name + '"]');
  if (el) {
    el.setAttribute('content', content);
  } else {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    el.setAttribute('content', content);
    document.head.appendChild(el);
  }
}

function setLink(rel, href) {
  var el = document.querySelector('link[rel="' + rel + '"]');
  if (el) {
    el.setAttribute('href', href);
  }
}

function updateHreflang(pagePath) {
  // Generic 'zh' aliases zh-TW so Chinese-language searchers outside Taiwan
  // (HK, Macau, SG, MY) resolve to the Traditional page; x-default is English
  // for every other language. The same set is emitted by scripts/gen-sitemap.js.
  var langs = { 'zh-TW': 'zh-tw', 'zh': 'zh-tw', 'en': 'en', 'ja': 'ja' };
  Object.keys(langs).forEach(function(hreflang) {
    var el = document.querySelector('link[hreflang="' + hreflang + '"]');
    if (el) {
      el.setAttribute('href', BASE_URL + '/' + langs[hreflang] + pagePath);
    }
  });
  var def = document.querySelector('link[hreflang="x-default"]');
  if (def) {
    def.setAttribute('href', BASE_URL + '/en' + pagePath);
  }
}

export { updateSEO, updateProductSEO, PRODUCT_SEO };
